import { useEffect, lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import Home from "./pages/Home";
import CinematicScrollBackground from "./components/CinematicScrollBackground";

// Lazy-load all non-home pages and non-critical components to minimize initial JS bundle
const Toaster = lazy(() => import('@/components/ui/sonner').then(m => ({ default: m.Toaster })));
const Services = lazy(() => import("./pages/Services"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
const Work = lazy(() => import("./pages/Work"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const AGB = lazy(() => import("./pages/AGB"));
const Widerruf = lazy(() => import("./pages/Widerruf"));
const Cookies = lazy(() => import("./pages/Cookies"));
const WiderrufsButton = lazy(() => import("./pages/WiderrufsButton"));
const ServiceAgreement = lazy(() => import("./pages/ServiceAgreement"));
const AdminWithdrawals = lazy(() => import("./pages/AdminWithdrawals"));
const AdminInvoices = lazy(() => import("./pages/AdminInvoices"));
const FAQ = lazy(() => import("./pages/FAQ"));
const SocialProof = lazy(() => import("./pages/SocialProof"));
const BlogPricing = lazy(() => import("./pages/BlogPricing"));
const BlogTimeline = lazy(() => import("./pages/BlogTimeline"));
const BlogLocalBusiness = lazy(() => import("./pages/BlogLocalBusiness"));
const CookieConsent = lazy(() => import('./components/CookieConsent'));
const LocalBusinessSchema = lazy(() => import('./components/LocalBusinessSchema').then(m => ({ default: m.LocalBusinessSchema })));

// Scroll to top on every route change — ensures clean navigation
function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location]);
  return null;
}

function ArticleScrollFade() {
  const [location] = useLocation();

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sections = Array.from(document.querySelectorAll<HTMLElement>('article > section:not(:first-child)'));

    sections.forEach(section => section.classList.add('reveal'));
    if (reducedMotion.matches) {
      sections.forEach(section => section.classList.add('visible'));
      return;
    }

    const observer = new IntersectionObserver(
      entries => entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      }),
      { threshold: 0.1, rootMargin: '0px 0px -8%' }
    );

    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, [location]);

  return null;
}

function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <>
      <ScrollToTop />
      <ArticleScrollFade />
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/services" component={Services} />
        <Route path="/work" component={Work} />
        <Route path="/pricing" component={Pricing} />
        <Route path="/contact" component={Contact} />
        <Route path="/faq" component={FAQ} />
        <Route path="/social" component={SocialProof} />
        <Route path="/blog/pricing" component={BlogPricing} />
        <Route path="/blog/timeline" component={BlogTimeline} />
        <Route path="/blog/local-business" component={BlogLocalBusiness} />
        <Route path="/impressum" component={Impressum} />
        <Route path="/datenschutz" component={Datenschutz} />
        <Route path="/agb" component={AGB} />
        <Route path="/widerruf" component={Widerruf} />
        <Route path="/cookies" component={Cookies} />
        <Route path="/widerrufsbutton" component={WiderrufsButton} />
        <Route path="/service-agreement" component={ServiceAgreement} />
        <Route path="/admin/withdrawals" component={AdminWithdrawals} />
        <Route path="/admin/invoices" component={AdminInvoices} />
        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <LanguageProvider>
          <TooltipProvider>
            <Suspense fallback={null}>
              <Toaster />
              <LocalBusinessSchema />
              <CinematicScrollBackground />
              <div className="site-content-layer">
                <Router />
                <CookieConsent />
              </div>
            </Suspense>
          </TooltipProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
